import { Component, ElementRef, model, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { AppGlobals } from '../../app.globals';
import { CommonModule } from '@angular/common';
import { DxDrawerModule, DxMenuModule } from 'devextreme-angular';
import { FeatureFlags } from 'src/app/interfaces/enums/feature-flag';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { SvgIconComponent } from '../../components/svg-icon/svg-icon.component';
import { IconName } from 'src/app/helpers/icon-exports';
import { LogoService } from 'src/app/services/logo.service';
import { SafeUrl } from '@angular/platform-browser';
import { AppConfig } from 'src/app/app.config';

export interface MenuItem {
  id: string;
  text: string;
  icon?: IconName;
  link?: string;
  admin?: boolean;
  features?: FeatureFlags[];
  subMenu?: MenuItem[];
  onClick?: () => void;
}
/** Admin entries are listed alphabetically by their translated label, so the order is right in every language. */
function byLocalizedText(a: MenuItem, b: MenuItem): number {
  return a.text.localeCompare(b.text, $localize.locale, { sensitivity: 'base' });
}

@Component({
    selector: 'app-drawer',
    templateUrl: './app-drawer.component.html',
    styleUrls: ['./app-drawer.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, DxDrawerModule, DxMenuModule, RouterLink, SvgIconComponent]
})
export class DrawerComponent implements OnInit {
  isDrawerOpen = model<boolean>(false);
  sideNavHeadings: MenuItem[] = [];
  bottomSideNavHeadings: MenuItem[] = [];
  readonly CLOSED_DRAWER_WIDTH = 62;
  maxContentWidth = this.CLOSED_DRAWER_WIDTH;
  showText: boolean = false;
  showSmallLogo: boolean = false;
  activatedMenuItem: string = '';
  activatedSubMenuItem: string = '';
  activeSubMenu: MenuItem[] | null = null;
  version = AppConfig.APPVersion;

  sideNavItems: MenuItem[] = [
    {
      id: 'dashboard',
      text: $localize`:@@project-pipeline:Project Pipeline`,
      icon: 'home', // Using available icon as placeholder
      link: 'dashboard',
      admin: false,
      features: [FeatureFlags.Dashboard],
    },
    {
      id: 'sites',
      text: $localize`:@@sites:Sites`,
      icon: 'location', // Using available icon as placeholder
      link: 'grid-connections',
      admin: false,
    },
    {
      id: 'projects',
      text: $localize`:@@projectList:Projects`,
      icon: 'folder-library', // Using available icon as placeholder
      link: 'projects',
      admin: false,
    },
    {
      id: 'dynamic-tables',
      text: $localize`:@@mytables:My Tables`,
      icon: 'layout-table', // Using available icon as placeholder
      link: 'tables',
      admin: false,
    },
    {
      id: 'user-tasks',
      text: $localize`:@@usertasklist:User Task List`,
      icon: 'check-list', // Using available icon as placeholder
      link: 'user-tasklist',
      admin: false,
    },
    {
      id: 'contracts',
      text: $localize`:@@contracts:Contracts`,
      icon: 'contracts', // Using available icon as placeholder
      link: 'contracts',
      admin: false,
      features: [FeatureFlags.Contracts],
    },
    {
      id: 'projectcost-overview',
      text: $localize`:@@baukostenUbersicht:Project Cost Overview`,
      icon: 'coins-euro', // Using available icon as placeholder
      link: 'projectcost-overview',
      admin: false,
      features: [FeatureFlags.ProjectCost],
    },
    {
      id: 'contacts',
      text: $localize`:@@contacts:Contacts`,
      icon: 'user-multiple', // Using available icon
      link: 'contacts/companies',
      admin: false,
    },
    {
      id: 'tech-components',
      text: $localize`:@@componentLibary:Component Libary`,
      icon: 'layers', // Using available icon as placeholder
      link: 'tech-components',
      admin: false,
      features: [FeatureFlags.TechnicalComponents],
    },
    {
      id: 'wiki',
      text: $localize`:@@wiki:Wiki`,
      icon: 'book-open', // Using available icon as placeholder
      link: 'wiki',
      admin: false,
      features: [FeatureFlags.Wiki],
    },
    {
      id: 'admin',
      text: $localize`:@@administration:Administration`,
      icon: 'settings', // Using available icon as placeholder
      admin: true,
      subMenu: [
        {
          id: 'admin-activity-log',
          text: $localize`:@@activityLog:Activity Log`,
          link: '/config/activity-log',
          admin: true,
        },
        {
          id: 'admin-general-config',
          text: $localize`:@@general:General`,
          link: '/config/general-config',
          admin: true,
        },
        {
          id: 'admin-traffic-light-config',
          text: $localize`:@@trafficLightConfig:Traffic Light Config`,
          link: '/config/traffic-light-config',
          admin: true,
        },
        {
          id: 'admin-database-export',
          text: $localize`:@@databaseExport:Database Export`,
          link: '/config/database-export',
          admin: true,
        },
        {
          id: 'admin-email-report-config',
          text: $localize`:@@emailReportConfig:Email Report Config`,
          link: '/config/email-report-config',
          admin: true,
        },
        {
          id: 'admin-property-config',
          text: $localize`:@@propertyConfig:Property Config`,
          link: '/config/property-config',
          admin: true,
        },
        {
          id: 'admin-export-config',
          text: $localize`:@@exportConfig:Export Lists`,
          link: '/config/export-config',
          admin: true,
        },
        {
          id: 'admin-folder-config',
          text: $localize`:@@folders:Folders`,
          link: '/config/folder-config',
          admin: true,
        },
        {
          id: 'admin-template-config',
          text: $localize`:@@templatesConfig:Templates`,
          link: '/config/template-config',
          admin: true,
          features: [FeatureFlags.TemplateManagement],
        },
        {
          id: 'admin-societies-config',
          text: $localize`:@@gesellschaften:Gesellschaften`,
          link: '/config/societies-config',
          admin: true,
          features: [FeatureFlags.Gesellschaften],
        },
        {
          id: 'admin-tags',
          text: $localize`:@@tags:Tags`,
          link: '/config/tags',
          admin: true,
        },
        {
          id: 'admin-cost-category-config',
          text: $localize`:@@costCategoryConfig:Cost Category Config`,
          link: '/config/cost-category-config',
          admin: true,
        },
        {
          id: 'admin-templates-costs-templates',
          text: $localize`:@@templatesCostsTemplates:Costs Templates`,
          link: '/config/templates-costs-templates',
          admin: true,
        },
        {
          id: 'admin-user-permissions',
          text: $localize`:@@userPermissions:User Permissions`,
          link: '/config/user-permissions',
          admin: true,
        },
        {
          id: 'project-config',
          text: $localize`:@@projectConfiguration:Project Configuration`,
          link: '/config/project-config',
          admin: true,
        },

        {
          id: 'tech-components-config',
          text: $localize`:@@techComponentsConfig:Tech Components Config`,
          link: '/config/technical-components-config',
          admin: true,
        },

        {
          id: 'admin-contracts-config',
          text: $localize`:@@contractsConfig:Contracts Config`,
          link: '/config/contracts-config',
          admin: true,
          features: [FeatureFlags.Contracts],
        },
      ].sort(byLocalizedText),
    },
  ];

  bottomMenuItems: MenuItem[] = [
    {
      id: 'feedback',
      text: $localize`:@@feedback:Feedback`,
      icon: 'comment',
      link: 'feedback',
      admin: false,
    },
    {
      id: 'logs',
      text: $localize`:@@logs:Logs`,
      icon: 'clock',
      link: null,
      admin: false,
      onClick: () => {
        this.logData();
      },
    },
    {
      id: 'logout',
      text: $localize`:@@logout:Logout`,
      icon: 'logout',
      link: 'logout',
      admin: false,
    },
  ];

  @ViewChildren('drawerItemList')
  drawerItems: QueryList<ElementRef>;
  smallLogo: SafeUrl = 'assets/images/branding/gpdone/logo-small.png';
  largeLogo: SafeUrl = 'assets/images/branding/gpdone/logo.png';
  favicon: SafeUrl = 'assets/images/branding/gpdone/favicon.ico';
  constructor(
    public appGlobals: AppGlobals,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private logoService: LogoService
  ) { }

  ngOnInit(): void {
    this.sideNavHeadings = this.filterMenuItems(this.sideNavItems);
    this.bottomSideNavHeadings = this.bottomMenuItems;
    this.isDrawerOpen.subscribe(val => this.toggleDrawer(val));
    this.findMenuIndex();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        AppGlobals.SetEditWarningStatus(false);
        this.findMenuIndex();
        if (!this.router.url.includes('/config')) {
          this.activeSubMenu = null;
        }
      }
    });

    this.loadLogos();
  }

  loadLogos() {
    this.logoService.eventEmitter.subscribe(data => {
      this.largeLogo = data.largeLogo ?? 'assets/images/branding/gpdone/logo.png';
      this.smallLogo = data.smallLogo ?? 'assets/images/branding/gpdone/logo-small.png';
      this.favicon = data.favicon ?? 'assets/images/branding/gpdone/favicon.ico';
    });
  }

  findMenuIndex() {
    let lastMenuId: string | null = null;
    let route = this.activatedRoute.firstChild;

    while (route) {
      if (route.snapshot.data['menuId']) {
        lastMenuId = route.snapshot.data['menuId'];
      }
      route = route.firstChild; // Move to the next child route
    }

    this.activatedMenuItem = lastMenuId;
  }

  toggleDrawer(isOpen): void {
    if (!isOpen) {
      this.maxContentWidth = this.CLOSED_DRAWER_WIDTH;
      this.showSmallLogo = false;
      setTimeout(() => {
        this.showText = false;
      }, 300);
    } else {
      let maxWidth = 0;
      this.showText = true;
      setTimeout(() => {
        this.drawerItems.forEach(item => {
          const width = item.nativeElement.scrollWidth; // Use scrollWidth to include content
          if (width > maxWidth) {
            maxWidth = width;
          }

          this.maxContentWidth = Math.max(maxWidth + 16, this.CLOSED_DRAWER_WIDTH);
          this.showSmallLogo = true;
        });
      }, 100);
    }
  }

  filterMenuItems(items: MenuItem[]): MenuItem[] {
    return items
      .filter(
        item =>
          (!item.admin || AppGlobals.UserPermissions.isAdministrator) &&
          (!item.features || item.features.every(feature => AppGlobals.UserCan(feature)))
      )
      .map(item => ({
        ...item,
        subMenu: item.subMenu
          ? item.subMenu.filter(tab => !tab.features || tab.features.every(feature => AppGlobals.UserCan(feature)))
          : undefined,
      }));
  }

  logData(): void {
    this.appGlobals.$showLogs.next(true);
  }

  onDrawerItemClick(item: MenuItem, clickedOnParent: boolean): void {
    if (clickedOnParent && !item.subMenu?.length) {
      this.activatedMenuItem = item.id;
      this.activeSubMenu = null;
      this.activatedSubMenuItem = null;
    }

    if (clickedOnParent && item.subMenu?.length) {
      this.activatedMenuItem = item.id;
      if (this.activeSubMenu) {
        this.activeSubMenu = null;
        return;
      }
      this.activeSubMenu = item.subMenu;
    }

    if (!clickedOnParent) {
      this.activatedSubMenuItem = item.id;
    }

    if (item.link) {
      this.router.navigate([item.link]);
    }

    if (item.onClick) {
      item.onClick();
    }
  }
}
